import { Link, useLoaderData, useLocation, useNavigate } from '@remix-run/react'
import { useEffect, useState } from 'react'
import type { ReactElement, ReactNode } from 'react'
import { BannerCashback } from './CashbackCard/BannerCashback'
import MyPrivacy from './MyPrivacy'
import { EmptyCart, MyAddress, MyCards, MyPersonalData } from '~/design-system/components'
import { Card, HeaderCard } from '~/design-system/primitives'

import { ChevronRight, CreditCard, DeviceReset, HomePin, HowToReg, Logout, Passkey, PersonPin, SocioS } from '~/icons'
import { cn } from '~/lib/cn'
import type { loader } from '~/routes/myaccount'
import type { Profile, ProfilePj, User } from '~/type/User'
import SocioSwift from './SocioSwift'
import { IsNotSocioBanner } from './CashbackCard/IsNotSocioBanner'

interface SidebarProps {
  setSelectedComponent?: Dispatch<SetStateAction<ReactElement | undefined>>
}

interface MenuItemsProps {
  firstIcon: ReactNode
  label: string
  path: string
  component?: ReactElement
  lastIcon?: ReactNode
  selectedMenu: boolean
}

export default function Sidebar() {
  const { user, cashbackBallance } = useLoaderData<typeof loader>()
  const navigate = useNavigate()
  const location = useLocation()

  const handleNavigation = (path: string) => {
    window.scrollTo(0, 0)
    navigate(path)
  }
  const isCorporate = user?.profile?.isCorporate


  const currentConsents = user?.profile?.consents?.reduce((acc, consent) => {
    acc[consent.key] = consent.value;
    return acc;
  }, {} as Record<string, boolean>);


  const MORNING_END = 12
  const AFTERNOON_END = 18

  const getGreeting = (): string => {
    const currentHour = new Date().getHours()
    if (currentHour < MORNING_END) return 'bom dia!'
    if (currentHour < AFTERNOON_END) return 'boa tarde!'
    return 'boa noite!'
  }

  const getUserGreeting = (): string => {
    if (user?.profile?.firstName) {
      return `Olá ${user.profile.firstName}, ${getGreeting()}`
    } else {
      return `Olá Usuário, ${getGreeting()}`
    }
  }

  const cardHolder: string = user?.profile ? user.profile.firstName + ' ' + user.profile.lastName : ''
  const documentHolder: string = user?.profile?.document ?? ''

  const MenuItems: MenuItemsProps[] = [
    {
      firstIcon: <PersonPin />,
      label: 'Dados Cadastrais',
      path: '/myaccount/dados-cadastrais',
      component: <MyPersonalData />,
      lastIcon: <ChevronRight />,
      selectedMenu: location.pathname === '/myaccount/dados-cadastrais',
    },
    {
      firstIcon: <HomePin />,
      label: 'Endereços',
      path: '/myaccount/enderecos',
      component: <MyAddress />,
      lastIcon: <ChevronRight />,
      selectedMenu: location.pathname === '/myaccount/enderecos',
    },
    {
      firstIcon: <SocioS />,
      label: 'Sócio Swift',
      path: '/myaccount/socio-swift',
      lastIcon: <ChevronRight />,
      component: <SocioSwift />,
      selectedMenu: location.pathname === '/myaccount/socio-swift',
    },
    {
      firstIcon: <CreditCard />,
      label: 'Meus Cartões',
      path: '/myaccount/meus-cartoes',
      component: <MyCards cardHolder={cardHolder} documentHolder={documentHolder} />,
      lastIcon: <ChevronRight />,
      selectedMenu: location.pathname === '/myaccount/meus-cartoes',
    },
    {
      firstIcon: <Passkey />,
      label: 'Privacidade',
      path: '/myaccount/privacidade',
      component: <MyPrivacy />,
      lastIcon: <ChevronRight />,
      selectedMenu: location.pathname === '/myaccount/privacidade',
    },
    {
      firstIcon: <DeviceReset />,
      label: 'Histórico de Pedidos',
      path: '/myaccount/historico-pedidos',
      component: <EmptyCart />,
      lastIcon: <ChevronRight />,
      selectedMenu: location.pathname === '/myaccount/historico-pedidos',
    },
    // {
    //   firstIcon: <FavoriteFilled />,
    //   label: 'Meus Favoritos',
    //   component: <EmptyCard />,
    //   lastIcon: <ChevronRight />,
    //   selectedMenu: false,
    // },
    // {
    //   firstIcon: <Sell />,
    //   label: 'Vale Compras',
    //   component: <EmptyCart />,
    //   lastIcon: <ChevronRight />,
    //   selectedMenu: false,
    // },
    {
      firstIcon: <Logout />,
      label: 'Sair',
      path: '/logout',
      component: <EmptyCart />,
      selectedMenu: false,
    },
  ]

  function handleLogout() {
    navigate('/logout')
  }

  return (
    <Card className={cn('lg:w-2/6 m-0 self-stretch')} color="primary" border>
      <HeaderCard description={getUserGreeting()} icon={<HowToReg />} />

      {currentConsents?.swift_vc && !cashbackBallance?.error && !isCorporate && (
        <BannerCashback cashbackBallance={cashbackBallance} />
      )}

      {!isCorporate && !currentConsents?.swift_vc && (
        <IsNotSocioBanner />
      )}

      {
        MenuItems.map((item, index) => {
          if (item.label === 'Sócio Swift' && isCorporate) {
            return null;
          }

          if (item.label === 'Sair') {
            return (
              <Card
                className={`cursor-pointer parentMenu flex-row justify-between ${item.selectedMenu && 'bg-gray-20'} p-2`}
                key={index}
                onClick={handleLogout}
              >
                <Card className={`cursor-pointer items-center flex flex-row p-0`}>
                  {item.firstIcon}
                  <span className="text-primary-black text-base ml-2">{item.label}</span>
                </Card>
                {item.lastIcon}
              </Card>
            );
          }

          return (
            <Link
              className="no-underline"
              key={index}
              to={item.path}
              onClick={() => handleNavigation(item.path)}
            >
              <Card
                className={`cursor-pointer parentMenu flex-row justify-between ${item.selectedMenu && 'bg-gray-20'} p-2`}
              >
                <Card className={`cursor-pointer items-center flex flex-row p-0`}>
                  {item.firstIcon}
                  <span className="text-primary-black text-base ml-2">{item.label}</span>
                </Card>
                {item.lastIcon}
              </Card>
            </Link>
          );
        })
      }

    </Card >
  )
}
